/**
 * Constantes de la vida del avatar (brief del taller del 5-nov, tanda 8).
 *
 * Un solo módulo para todos los números de las capas nuevas: micro-desvíos
 * de mirada en la idle, gestos acoplados al habla, modulación lenta de la
 * expresión, el decay al terminar el audio, la capa "pensando" y el
 * pre-roll. Son **defaults
 * iniciales de calibración**, no verdad revelada: `/dev/avatar` los expone
 * en el panel "Vida" para ajustarlos mirando el avatar, y los que queden
 * buenos vuelven acá.
 *
 * Salen en el bloque `vida` de `calibracion.json` (versión 3): el equipo
 * los ajusta en `/dev/avatar`, descarga el archivo y lo que quede bueno
 * se copia acá como default. Hoy son globales, no por avatar: la app no
 * lee el archivo en runtime.
 *
 * Unidades: la mirada en unidades de `mirada_*` (±1 = ±20° de yaw, ±15° de
 * pitch), los ángulos de cabeza en grados, los pesos en `0..1`.
 */

/** Herramientas de azar y tiempo compartidas por las capas. */
export type Rng = () => number;

/** Número uniforme en `[min, max)`. */
export function entre(rng: Rng, min: number, max: number): number {
  return min + rng() * (max - min);
}

/** `+1` o `-1` con la misma probabilidad. */
export function signo(rng: Rng): 1 | -1 {
  return rng() < 0.5 ? -1 : 1;
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Curva de arranque y llegada suaves, sin overshoot. */
export function smoothstep(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

export const GRADOS_A_RAD = Math.PI / 180;

export interface VidaOpts {
  // --- Pieza 1: micro-desvíos de mirada en la idle ---

  /** Amplitud horizontal de un desvío, en valor absoluto. */
  miradaIdleXMin: number;
  miradaIdleXMax: number;
  /** Amplitud vertical de un desvío, en valor absoluto. */
  miradaIdleYMin: number;
  miradaIdleYMax: number;
  /** Intervalo entre saltos de mirada, con jitter uniforme. */
  miradaIdleIntervaloMinMs: number;
  miradaIdleIntervaloMaxMs: number;
  /** Fracción de los saltos que vuelven al centro (a cámara). */
  miradaIdleProbCentro: number;
  /**
   * Duración de una sacada. Rápida a propósito: una mirada que se desliza
   * en cientos de milisegundos se lee de muñeco.
   */
  sacadaMinMs: number;
  sacadaMaxMs: number;

  // --- Pieza 2: gestos acoplados al habla ---

  /** El nivel tiene que superar su media móvil por este factor. */
  enfasisFactor: number;
  /** Tiempo mínimo entre dos énfasis: si no, dispara en cada sílaba. */
  enfasisRefractarioMs: number;
  /** Constante de tiempo de la media móvil del nivel. */
  enfasisMediaMs: number;
  /**
   * Al empezar un enunciado la media todavía no representa a la voz: se
   * deja converger este tiempo antes de detectar.
   */
  enfasisCalentamientoMs: number;
  /** Nivel mínimo para un énfasis: un pico sobre casi silencio no lo es. */
  enfasisNivelMin: number;

  /** Asentimiento: cabeceo hacia abajo por énfasis, en grados. */
  asentimientoMinGrados: number;
  asentimientoMaxGrados: number;
  asentimientoAtaqueMs: number;
  asentimientoCaidaMs: number;

  /** Deriva de cabeza mientras hay voz: ruido lento en yaw y roll. */
  derivaYawGrados: number;
  derivaRollGrados: number;
  derivaHz: number;
  /** Cuánto tarda la deriva en entrar y salir al empezar y terminar el audio. */
  derivaFundidoMs: number;

  /** Pulso de cejas por énfasis: suma a `browInnerUp` y `browOuterUp*`. */
  cejasPulsoMin: number;
  cejasPulsoMax: number;
  cejasAtaqueMs: number;
  cejasCaidaMs: number;

  /** Probabilidad de parpadear al empezar una pausa. */
  parpadeoPausaProb: number;
  /** Una pausa cuenta como tal después de sostenerse este tiempo. */
  pausaMinMs: number;
  /**
   * Multiplica la tasa basal de parpadeo de la idle mientras suena el
   * audio. Menos de 1: el parpadeo pasa a caer en las pausas.
   */
  parpadeoTasaHablando: number;

  /** Mirada al iniciar un enunciado: desvío horizontal breve. */
  miradaInicioMin: number;
  miradaInicioMax: number;
  miradaInicioMinMs: number;
  miradaInicioMaxMs: number;
  /** Silencio que separa dos enunciados dentro del mismo audio. */
  pausaEnunciadoMs: number;

  /**
   * Tope de la suma de todos los gestos sobre la cabeza, por eje. El
   * estándar de rig (`06` §3) fija límites de los ojos y no de la cabeza:
   * éste es nuestro, para que ninguna suma de capas se lea como un tic.
   */
  cabezaMaxGrados: number;

  // --- Pieza 3: modulación lenta de la expresión ---

  /**
   * Los pesos de la expresión se multiplican por
   * `f = 1 + a·ruido + b·enfasis`, recortado a `[min, max]`. `a` es la
   * amplitud del ruido lento, siempre presente.
   */
  modulacionRuido: number;
  /** Frecuencia del ruido lento. */
  modulacionHz: number;
  /** `b`: cuánto suma un énfasis de la voz. Solo actúa mientras hay voz. */
  modulacionEnfasis: number;
  /** Subida y bajada del aporte de un énfasis. */
  modulacionEnfasisAtaqueMs: number;
  modulacionEnfasisCaidaMs: number;
  /**
   * Piso del factor. Protege la legibilidad: por debajo de ~0,85 una
   * expresión empieza a no reconocerse (`08_CALIBRACION.md` §3).
   */
  modulacionMin: number;
  modulacionMax: number;

  // --- Pieza 4: decay de la expresión al terminar el audio ---

  /**
   * Cuánto se sostiene la expresión después de que termina de sonar el
   * audio. Con respuestas cortas, un público de 25 personas no alcanza a
   * verla si se corta con la voz.
   */
  retencionMs: number;
  /** Duración de la transición de vuelta a `NEUTRAL` después de la retención. */
  decaimientoMs: number;

  // --- Pieza 5: capa "pensando" ---

  /** Cuánto tarda en empezar a "pensar" después de entrar en `procesando`. */
  pensandoRetrasoMs: number;
  /** Mirada fuera de cámara: horizontal, a un lado al azar. */
  pensandoMiradaXMin: number;
  pensandoMiradaXMax: number;
  /** Mirada fuera de cámara: vertical, hacia arriba. */
  pensandoMiradaYMin: number;
  pensandoMiradaYMax: number;
  /** Cuánto mira afuera antes de un regreso breve a cámara. */
  pensandoFueraMinMs: number;
  pensandoFueraMaxMs: number;
  /** Cuánto dura ese regreso a cámara. */
  pensandoRegresoMinMs: number;
  pensandoRegresoMaxMs: number;
  /** Labios levemente apretados: peso de `mouthPress*`. */
  pensandoPresionMin: number;
  pensandoPresionMax: number;
  /** Rampa de entrada y salida de los labios. */
  pensandoRampaMs: number;
  /** Multiplica la tasa basal de parpadeo mientras piensa: parpadea más lento. */
  pensandoTasaParpadeo: number;

  // --- Pieza 5: pre-roll ---

  /**
   * Transición de entrada a la expresión que llega con la respuesta. Más
   * corta que la del motor (800 ms): tiene que estar puesta cuando arranca
   * la voz.
   */
  transicionEntradaMs: number;
  /** Retraso entre la llegada del audio y el inicio de la reproducción. */
  prerollMs: number;
  /** "Inspiración": los últimos ms del pre-roll. */
  inspiracionMs: number;
  /** Apertura de `jawOpen` en la inspiración. */
  inspiracionJawMin: number;
  inspiracionJawMax: number;
  /** Cabeceo hacia arriba en la inspiración, en grados. */
  inspiracionPitchGrados: number;
  /** Cuánto tarda en soltarse la inspiración cuando arranca la voz. */
  inspiracionSoltarMs: number;
}

export const VIDA_DEFECTOS: Readonly<VidaOpts> = {
  miradaIdleXMin: 0.08,
  miradaIdleXMax: 0.15,
  miradaIdleYMin: 0.05,
  miradaIdleYMax: 0.1,
  miradaIdleIntervaloMinMs: 2000,
  miradaIdleIntervaloMaxMs: 5000,
  miradaIdleProbCentro: 0.65,
  sacadaMinMs: 50,
  sacadaMaxMs: 80,

  enfasisFactor: 1.5,
  enfasisRefractarioMs: 600,
  enfasisMediaMs: 800,
  enfasisCalentamientoMs: 300,
  enfasisNivelMin: 0.3,

  /**
   * Asentimiento **apagado** para el taller (decisión del 2026-10-10). En la
   * prueba humana se leía como un tic; además, asentir afirma, y saber si
   * el paciente afirma o niega requiere información que el contrato hoy no
   * trae. Queda el canal, en cero. Ver `BACKLOG.md` C-18.
   */
  asentimientoMinGrados: 0,
  asentimientoMaxGrados: 0,
  asentimientoAtaqueMs: 70,
  asentimientoCaidaMs: 250,

  derivaYawGrados: 2,
  derivaRollGrados: 1,
  derivaHz: 0.15,
  derivaFundidoMs: 400,

  cejasPulsoMin: 0.1,
  cejasPulsoMax: 0.2,
  cejasAtaqueMs: 80,
  cejasCaidaMs: 300,

  parpadeoPausaProb: 0.4,
  pausaMinMs: 150,
  parpadeoTasaHablando: 0.6,

  miradaInicioMin: 0.15,
  miradaInicioMax: 0.25,
  miradaInicioMinMs: 500,
  miradaInicioMaxMs: 1500,
  pausaEnunciadoMs: 400,

  cabezaMaxGrados: 6,

  modulacionRuido: 0.08,
  modulacionHz: 0.25,
  modulacionEnfasis: 0.1,
  modulacionEnfasisAtaqueMs: 80,
  modulacionEnfasisCaidaMs: 400,
  modulacionMin: 0.85,
  modulacionMax: 1.08,

  retencionMs: 1500,
  decaimientoMs: 2500,

  pensandoRetrasoMs: 400,
  pensandoMiradaXMin: 0.2,
  pensandoMiradaXMax: 0.3,
  pensandoMiradaYMin: 0.15,
  pensandoMiradaYMax: 0.2,
  pensandoFueraMinMs: 1500,
  pensandoFueraMaxMs: 3000,
  pensandoRegresoMinMs: 300,
  pensandoRegresoMaxMs: 600,
  pensandoPresionMin: 0.08,
  pensandoPresionMax: 0.12,
  pensandoRampaMs: 300,
  pensandoTasaParpadeo: 0.7,

  transicionEntradaMs: 400,
  prerollMs: 300,
  inspiracionMs: 200,
  inspiracionJawMin: 0.06,
  inspiracionJawMax: 0.1,
  inspiracionPitchGrados: 1,
  inspiracionSoltarMs: 150,
};

/** Interruptores de las capas nuevas. Todas encendidas en el producto. */
export interface CapasVida {
  /** Micro-desvíos de mirada de la idle (pieza 1). */
  miradaIdle: boolean;
  /** Gestos acoplados al habla (pieza 2). */
  gestosHabla: boolean;
  /** Modulación lenta de la expresión (pieza 3). */
  modulacion: boolean;
  /**
   * Retención y decay al terminar el audio (pieza 4). Apagado, la
   * expresión vuelve a `NEUTRAL` en cuanto termina de sonar, como antes
   * de la tanda 8. Lo aplica el orquestador, no el adapter.
   */
  decaimiento: boolean;
  /**
   * Capa "pensando" mientras se procesa el turno (pieza 5). La activa el
   * orquestador; el adapter la ignora si está apagada.
   */
  pensando: boolean;
  /**
   * Pre-roll (pieza 5): transición de entrada corta, retraso antes de
   * reproducir e inspiración. Apagado, la expresión entra con la
   * transición del motor y el audio suena en cuanto llega.
   */
  preroll: boolean;
}

export const CAPAS_VIDA_DEFECTOS: Readonly<CapasVida> = {
  miradaIdle: true,
  gestosHabla: true,
  modulacion: true,
  decaimiento: true,
  pensando: true,
  preroll: true,
};

/**
 * Movimiento rápido de un punto de mirada a otro: una sacada.
 *
 * Interpola con `smoothstep` en un tiempo fijo y corto, en vez del
 * suavizado exponencial que usaba la idle hasta la tanda 8 — ese tardaba
 * cerca de un segundo en llegar y se veía como un ojo que se desliza.
 */
export class Sacada {
  private desdeX = 0;
  private desdeY = 0;
  private haciaX = 0;
  private haciaY = 0;
  private transcurridoMs = 0;
  private duracionMs = 0;
  x = 0;
  y = 0;

  /** Arranca desde donde está el ojo ahora: interrumpir no da un salto. */
  ir(x: number, y: number, duracionMs: number): void {
    this.desdeX = this.x;
    this.desdeY = this.y;
    this.haciaX = x;
    this.haciaY = y;
    this.transcurridoMs = 0;
    this.duracionMs = Math.max(1, duracionMs);
  }

  get destinoX(): number {
    return this.haciaX;
  }

  get destinoY(): number {
    return this.haciaY;
  }

  update(deltaMs: number): void {
    if (this.transcurridoMs >= this.duracionMs) return;
    this.transcurridoMs += deltaMs;
    const t = smoothstep(this.transcurridoMs / this.duracionMs);
    this.x = this.desdeX + (this.haciaX - this.desdeX) * t;
    this.y = this.desdeY + (this.haciaY - this.desdeY) * t;
  }

  reiniciar(): void {
    this.x = this.y = this.desdeX = this.desdeY = this.haciaX = this.haciaY = 0;
    this.transcurridoMs = this.duracionMs = 0;
  }
}

/**
 * Pulso de ataque y caída: sube a `amplitud` en `ataqueMs` y vuelve a
 * cero en `caidaMs`, los dos con curva suave. Redisparar a mitad de camino
 * arranca desde el valor actual, sin salto.
 */
export class Pulso {
  private fase: 'reposo' | 'ataque' | 'caida' = 'reposo';
  private transcurridoMs = 0;
  private desde = 0;
  private amplitud = 0;
  valor = 0;

  disparar(amplitud: number): void {
    this.desde = this.valor;
    this.amplitud = amplitud;
    this.fase = 'ataque';
    this.transcurridoMs = 0;
  }

  update(deltaMs: number, ataqueMs: number, caidaMs: number): number {
    if (this.fase === 'reposo') return 0;
    this.transcurridoMs += deltaMs;
    if (this.fase === 'ataque') {
      const t = smoothstep(this.transcurridoMs / Math.max(1, ataqueMs));
      this.valor = this.desde + (this.amplitud - this.desde) * t;
      if (t >= 1) {
        this.fase = 'caida';
        this.transcurridoMs = 0;
      }
      return this.valor;
    }
    const t = smoothstep(this.transcurridoMs / Math.max(1, caidaMs));
    this.valor = this.amplitud * (1 - t);
    if (t >= 1) {
      this.fase = 'reposo';
      this.valor = 0;
    }
    return this.valor;
  }

  get activo(): boolean {
    return this.fase !== 'reposo';
  }

  reiniciar(): void {
    this.fase = 'reposo';
    this.valor = 0;
  }
}

/**
 * Ruido suave en `-1..1`: nudos al azar cada `1 / hz` segundos, unidos con
 * `smoothstep`. Más orgánico que un seno —no se repite— y sin las
 * esquinas de un ruido interpolado linealmente.
 */
export class RuidoSuave {
  private anterior: number;
  private siguiente: number;
  private faseMs = 0;

  constructor(private readonly rng: Rng) {
    this.anterior = entre(rng, -1, 1);
    this.siguiente = entre(rng, -1, 1);
  }

  update(deltaMs: number, hz: number): number {
    const periodoMs = 1000 / Math.max(0.01, hz);
    this.faseMs += deltaMs;
    while (this.faseMs >= periodoMs) {
      this.faseMs -= periodoMs;
      this.anterior = this.siguiente;
      this.siguiente = entre(this.rng, -1, 1);
    }
    const t = smoothstep(this.faseMs / periodoMs);
    return this.anterior + (this.siguiente - this.anterior) * t;
  }
}
